import { pageVisibleOnce, type NativeBridge } from '@forest/native-services'
import { perfLogsEnabled, platform } from './params'

// Boot-performance diagnostics: a timeline of marks from navigation start
// (performance.timeOrigin — roughly when native began loading the WebView)
// through pageReady, which dismisses the native cover at boot, to the Shop
// reveal, which swaps the boot skeleton for the real Shop (ADR 0048), plus
// per-method native-call stats and inbound bridge transport counts, dumped
// as one summary when the reveal gate fires. Every line goes to the console
// and to window.logs (@forest/native-diagnostics) so a device without an
// inspector attached can still hand them over; an attached inspector reads
// the same data live through window.shopPerf. Off in production unless the
// launch URL carries ?perf (params.perfLogsEnabled); every entry point is
// then a no-op, so the probes cost nothing on the hot path.

const TAG = '[shop:perf]'

// window.logs keeps this many entries; the probe trims its own pushes to it.
const MAX_WINDOW_LOGS = 500

type Detail = Record<string, string | number | boolean | null | undefined>

interface Mark {
  at: number
  detail?: Detail
  label: string
}

const marks: Mark[] = []
const markedOnce = new Set<string>()
// Every console line, uncapped, for copy-pasting from the inspector.
const emitted: string[] = []

function round(ms: number): number {
  return Math.round(ms)
}

function formatDetail(detail?: Detail): string {
  if (!detail) return ''
  return Object.entries(detail)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(' ')
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function emit(line: string): void {
  console.log(`${TAG} ${line}`)
  emitted.push(line)
  // native-diagnostics mirrors only console.error into window.logs; perf
  // lines go there directly, under its cap.
  const logs = (window as { logs?: unknown }).logs
  if (Array.isArray(logs)) {
    logs.push(`[perf] ${line}`)
    while (logs.length > MAX_WINDOW_LOGS) logs.shift()
  }
}

// One point on the boot timeline, in ms since navigation start. Also a User
// Timing mark, so Safari's / Chrome's timeline shows it alongside network.
export function mark(label: string, detail?: Detail): void {
  if (!perfLogsEnabled) return
  const at = performance.now()
  marks.push({ at, detail, label })
  try {
    performance.mark(`shop:${label}`)
  } catch {
    // A WebView without User Timing still gets the console line.
  }
  emit(`+${round(at)}ms ${label} ${formatDetail(detail)}`.trimEnd())
}

// For effects that re-run: the first call with a label marks, later ones are
// ignored.
export function markOnce(label: string, detail?: Detail): void {
  if (!perfLogsEnabled || markedOnce.has(label)) return
  markedOnce.add(label)
  mark(label, detail)
}

// Brackets one boot step with `${label}:start` and `${label}:done` (or
// `:failed`) marks carrying its duration. Takes the promise itself so a call
// site can wrap an expression in place, or a thunk when the step's own inner
// marks should come after its start mark; the outcome is untouched.
export function timed<T>(
  label: string,
  task: Promise<T> | (() => Promise<T>),
  detail?: Detail,
): Promise<T> {
  if (!perfLogsEnabled) return typeof task === 'function' ? task() : task
  const start = performance.now()
  mark(`${label}:start`, detail)
  const promise = typeof task === 'function' ? task() : task
  return Promise.resolve(promise).then(
    (value) => {
      mark(`${label}:done`, { ...detail, ms: round(performance.now() - start) })
      return value
    },
    (error: unknown) => {
      mark(`${label}:failed`, {
        ...detail,
        ms: round(performance.now() - start),
        error: describeError(error),
      })
      throw error
    },
  )
}

// --- Native-call probe -----------------------------------------------------

interface CallStat {
  bytes: number
  count: number
  failed: number
  maxMs: number
  sumMs: number
}

const callStats = new Map<string, CallStat>()
let inflight = 0
let maxInflight = 0

function recordCall(
  key: string,
  ms: number,
  bytes: number,
  failed: boolean,
): void {
  const stat = callStats.get(key) ?? {
    bytes: 0,
    count: 0,
    failed: 0,
    maxMs: 0,
    sumMs: 0,
  }
  stat.count++
  if (failed) stat.failed++
  stat.sumMs += ms
  stat.maxMs = Math.max(stat.maxMs, ms)
  stat.bytes += bytes
  callStats.set(key, stat)
}

// A result's size as it crossed the bridge, roughly. Image results carry a
// base64 payload that dominates the message; its length is read directly so
// the probe never copies megabytes of image data on the boot path it is
// measuring. Everything else is small enough to stringify.
function approxSize(value: unknown): number {
  if (value === undefined || value === null) return 0
  if (typeof value === 'string') return value.length
  const data = (value as { data?: unknown }).data
  if (typeof data === 'object' && data !== null) {
    const base64 = (data as { base64?: unknown }).base64
    if (typeof base64 === 'string') return base64.length
  }
  try {
    return JSON.stringify(value)?.length ?? 0
  } catch {
    return 0
  }
}

type BifrostInterceptor = Parameters<
  NativeBridge['bifrost']['addInterceptor']
>[0]

// Round trip of every web→native call — from the bridge accepting the
// envelope (after the handshake) to its response landing — keyed by
// service.method. Wall clock per call, so sums overlap under concurrency;
// avg and max are the numbers to compare across platforms.
function createCallProbe(): BifrostInterceptor {
  return {
    name: 'ShopPerfProbe',
    // Ahead of every built-in interceptor so the measurement brackets them.
    order: 0,
    async intercept(direction, message, next) {
      if (
        direction !== 'outbound' ||
        !('kind' in message) ||
        message.kind !== 'call'
      ) {
        return next()
      }
      const key = `${message.service}.${message.method}`
      const start = performance.now()
      inflight++
      maxInflight = Math.max(maxInflight, inflight)
      try {
        const result: unknown = await next()
        recordCall(key, performance.now() - start, approxSize(result), false)
        return result
      } catch (error) {
        recordCall(key, performance.now() - start, 0, true)
        throw error
      } finally {
        inflight--
      }
    },
  }
}

// Hooks the call probe onto the freshly created bridge and marks the
// handshake's completion (native calls issued before that mark waited on
// it) and native's pageVisible — a WebView loaded before it is shown puts
// that mark late, and its pageReady time is not comparable to a cold one.
export function observeBridge(bridge: NativeBridge): void {
  if (!perfLogsEnabled) return
  mark('bridge:init')
  bridge.bifrost.addInterceptor(createCallProbe())
  void bridge.ready.then(
    () => mark('bridge:ready'),
    (error: unknown) => {
      mark('bridge:ready-failed', { error: describeError(error) })
      // Still unhandled on purpose: native-diagnostics keeps logging the
      // handshake failure exactly as it did before the probe existed.
      throw error
    },
  )
  void pageVisibleOnce.promise.then(() => mark('native:pageVisible'))
}

// --- Inbound transport probe -----------------------------------------------

const inbound = {
  chunkBytes: 0,
  chunks: 0,
  messages: 0,
  transfers: new Set<string>(),
}

interface ChunkLike {
  data: string
  transferId: string
  type: 'bifrost_chunk'
}

function isChunk(message: unknown): message is ChunkLike {
  return (
    typeof message === 'object' &&
    message !== null &&
    (message as { type?: unknown }).type === 'bifrost_chunk'
  )
}

type InboundHandler = (message: unknown) => void

// Native delivers every message — responses, calls, handshake acks, and the
// chunks a large response is split into — through window.bifrostHandleN2W.
// The bridge assigns that handler during initialize(); trapping the property
// beforehand lets the probe count what arrives without touching the bridge.
// Chunk counts are the tell for a transport limit: a platform that splits
// image responses into many chunks pays a bridge hop per chunk.
export function installInboundProbe(): void {
  if (!perfLogsEnabled) return
  const key = 'bifrostHandleN2W'
  const host = window as unknown as Record<string, InboundHandler | undefined>
  let handler = host[key]
  const wrapped: InboundHandler = (message) => {
    inbound.messages++
    if (isChunk(message)) {
      inbound.chunks++
      inbound.chunkBytes += message.data.length
      inbound.transfers.add(message.transferId)
    }
    handler?.(message)
  }
  try {
    Object.defineProperty(window, key, {
      configurable: true,
      enumerable: true,
      get: () => (handler ? wrapped : undefined),
      set: (next: InboundHandler | undefined) => {
        handler = next
      },
    })
  } catch {
    // A non-configurable property means no inbound counts; the rest of the
    // diagnostics still work.
  }
}

// --- Summary -----------------------------------------------------------------

// Resource lines the summary lists before truncating: the bundle, the API
// calls, and the remote card art all show up here, and dev serves hundreds
// of unbundled modules.
const MAX_RESOURCE_LINES = 60

function resourcePath(name: string): string {
  try {
    const url = new URL(name)
    return `${url.pathname}${url.search}`
  } catch {
    return name
  }
}

let revealReason: string | null = null
let bootSummary: string | null = null

// Navigation and resource timing (network waits, from the HTML itself
// through the API calls and remote art), the mark timeline, native-call
// stats by method, and inbound transport counts, as of now.
function buildBootSummary(reason: string): string {
  const lines = [
    `boot summary: reveal=${reason} at +${round(performance.now())}ms platform=${platform} ua="${navigator.userAgent}"`,
  ]
  if (typeof performance.getEntriesByType === 'function') {
    const [navigation] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[]
    if (navigation) {
      lines.push(
        `navigation: request=${round(navigation.requestStart)}ms first-byte=${round(navigation.responseStart)}ms html-done=${round(navigation.responseEnd)}ms dom-interactive=${round(navigation.domInteractive)}ms dcl=${round(navigation.domContentLoadedEventEnd)}ms load=${round(navigation.loadEventEnd)}ms`,
      )
    }
    const resources = performance.getEntriesByType(
      'resource',
    ) as PerformanceResourceTiming[]
    for (const entry of resources.slice(0, MAX_RESOURCE_LINES)) {
      lines.push(
        `resource[${entry.initiatorType}] ${resourcePath(entry.name)} start=+${round(entry.startTime)}ms dur=${round(entry.duration)}ms transfer=${entry.transferSize}B`,
      )
    }
    if (resources.length > MAX_RESOURCE_LINES) {
      lines.push(
        `resource: … ${resources.length - MAX_RESOURCE_LINES} more not listed`,
      )
    }
  }
  lines.push('timeline:')
  for (const { at, detail, label } of marks) {
    lines.push(`  +${round(at)}ms ${label} ${formatDetail(detail)}`.trimEnd())
  }
  const totalCalls = [...callStats.values()].reduce(
    (sum, stat) => sum + stat.count,
    0,
  )
  lines.push(
    `native calls: ${totalCalls} (max inflight ${maxInflight}; per-call wall clock, sums overlap under concurrency)`,
  )
  for (const [key, stat] of [...callStats].sort(
    (a, b) => b[1].sumMs - a[1].sumMs,
  )) {
    lines.push(
      `  ${key}: n=${stat.count} failed=${stat.failed} avg=${round(stat.sumMs / stat.count)}ms max=${round(stat.maxMs)}ms sum=${round(stat.sumMs)}ms bytes=${stat.bytes}`,
    )
  }
  lines.push(
    `inbound: messages=${inbound.messages} chunks=${inbound.chunks} chunked-transfers=${inbound.transfers.size} chunk-bytes=${inbound.chunkBytes}`,
  )
  return lines.join('\n')
}

// The one-shot report at reveal.
export function reportBootSummary(reason: string): void {
  if (!perfLogsEnabled) return
  revealReason = reason
  bootSummary = buildBootSummary(reason)
  emit(bootSummary)
}

// --- Inspector handle --------------------------------------------------------

// The collected diagnostics, live, for an attached inspector: `shopPerf.marks`
// is the timeline so far, `shopPerf.calls` the native-call stats by method,
// `shopPerf.inbound` the transport counts, and `shopPerf.summary` the boot
// summary as dumped at reveal (null until then). `shopPerf.report()` builds
// the summary from the current state at any point — before the reveal, or
// after later native calls have been added to the stats — and
// `shopPerf.lines` holds every console line for copy-pasting. Installed only
// when the diagnostics are on.
export interface ShopPerf {
  readonly calls: ReadonlyMap<string, Readonly<CallStat>>
  readonly inbound: Readonly<typeof inbound>
  readonly lines: readonly string[]
  readonly marks: readonly Mark[]
  report: () => string
  readonly summary: string | null
}

declare global {
  interface Window {
    shopPerf?: ShopPerf
  }
}

if (perfLogsEnabled) {
  window.shopPerf = {
    calls: callStats,
    inbound,
    lines: emitted,
    marks,
    report: () => buildBootSummary(revealReason ?? 'pending'),
    get summary() {
      return bootSummary
    },
  }
}
